import {Injectable} from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {LocalStorage} from "../../services/appstate/storage/local-storage";
import {AppStateService} from "../../services/appstate/app.state.service";
import {GlobalAuthService} from "../../services/global-auth.service";
import {environment} from '../../../environments/environment';
import {featureDisabledMessage} from '../../util/release-feature-flags.util';
import {mergeSmartDiningPrincipalHeaders} from './sd-principal-headers.util';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    // Définir le tableau des mots-clés à exclure
    private readonly EXCLUDED_URL_KEYWORDS: string[] = [
        'wasender',
        'activateAccountDeliver',
        /** Public account-manager activation (GET check + POST activate); stale JWT must not trigger refresh. */
        'adminAccountManagers/checkManagerAccount',
        'adminAccountManagers/activateAccountManager',
        'public',
        'auth/login',
        'auth/deliver-activation',
        /** Do not attach access JWT; refresh call must send only refresh JWT in Authorization. */
        'admins/refreshToken',
        /** Unauthenticated admin auth endpoints (avoid sending a stale Bearer on re-login). */
        'admins/signIn',
        'admins/forgetPassword',
        'admins/resetPassword',
        /** Login MFA completion without access JWT yet. */
        'admins/sendMFAConfirmCode',
        'admins/confirmMfaUserCode',
    ];

// Méthode pour vérifier si l'URL contient un mot-clé exclu
    private shouldExcludeAuth(url: string): boolean {
        return this.EXCLUDED_URL_KEYWORDS.some(keyword => url.includes(keyword));
    }

    /** Same base URL as {@link ConstantService.API_ENDPOINT} — mergeable Nest IAM for local / non-prod. */
    private isSmartDiningApiUrl(url: string): boolean {
        const base = (environment as {API_ENDPOINT?: string}).API_ENDPOINT?.replace(/\/+$/, '') ?? '';
        if (base && url.startsWith(base)) {
            return true;
        }
        return url.includes('/api/v1/');
    }

    constructor(
        private readonly appState: AppStateService,
        private readonly globalAuthService: GlobalAuthService,
    ) {
    }

    intercept(httpRequest: HttpRequest<any>, httpHandler: HttpHandler): Observable<HttpEvent<any>> {

        // Vérifier si l'URL doit être exclue de l'authentification
        const isExcluded = this.shouldExcludeAuth(httpRequest.url);

        /** Attach stored access JWT whenever present; rely on 401 + refresh for expiry (avoids false negatives if `exp` is missing or decode fails). */
        if (LocalStorage.existKey("token") && !isExcluded) {
            const token = LocalStorage.getToken();
            httpRequest = httpRequest.clone({
                setHeaders: {
                    'Authorization': 'Bearer ' + token,
                },
            });
        } else {
            httpRequest = httpRequest.clone({
                setHeaders: {
                    // Headers pour les requêtes sans authentification
                },
            });
        }

        if (
            !environment.production &&
            LocalStorage.existKey('token') &&
            !isExcluded &&
            this.isSmartDiningApiUrl(httpRequest.url)
        ) {
            httpRequest = httpRequest.clone({
                headers: mergeSmartDiningPrincipalHeaders(
                    httpRequest.headers,
                    this.appState,
                    environment.hrAdmin?.devHeaders,
                    true,
                ),
            });
        }

        return httpHandler.handle(httpRequest)
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    if (err.status === 503 && err.error?.error === 'FEATURE_DISABLED') {
                        const enriched = {
                            ...err.error,
                            userMessage: featureDisabledMessage(err.error?.feature),
                        };
                        return throwError(() => new HttpErrorResponse({ ...err, error: enriched }));
                    }
                    if (err.status === 401) {
                        /** signIn / forgetPassword etc.: 401 means bad credentials or similar — do not refresh or signOut (avoids races with login UI). */
                        if (isExcluded) {
                            return throwError(() => err);
                        }
                        const refreshToken = LocalStorage.getRefreshToken();
                        const rememberMe = this.appState.user.isRememberMe;
                        if (rememberMe) {
                            return this.handle401Error(httpRequest, httpHandler, refreshToken);
                        }
                        this.appState.reset();
                        this.globalAuthService.signOut();
                    }
                    return throwError(() => err);
                })
            );
    }

    private handle401Error(httpRequest: HttpRequest<any>, httpHandler: HttpHandler, refreshToken: string): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);
            if (refreshToken) {
                return this.globalAuthService.refreshToken({refreshToken}).pipe(
                    switchMap((result: any) => {
                        this.isRefreshing = false;
                        const data = result?.data ?? result;
                        const accessToken = data?.token;
                        const nextRefresh = data?.refreshToken;
                        if (!accessToken) {
                            this.appState.reset();
                            this.globalAuthService.signOut();
                            return throwError(() => new Error('Refresh response missing token'));
                        }
                        LocalStorage.setToken(accessToken);
                        if (nextRefresh) {
                            LocalStorage.setRefreshToken(nextRefresh);
                        }
                        this.refreshTokenSubject.next(accessToken);
                        return httpHandler.handle(this.addTokenHeader(httpRequest, accessToken));
                    }),
                    catchError((err) => {
                        this.isRefreshing = false;
                        this.appState.reset();
                        this.globalAuthService.signOut();
                        return throwError(() => err);
                    })
                );
            }
            this.isRefreshing = false;
            this.appState.reset();
            this.globalAuthService.signOut();
            return throwError(() => new Error('No refresh token'));
        }

        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap((token) => httpHandler.handle(this.addTokenHeader(httpRequest, token)))
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({headers: request.headers.set('Authorization', 'Bearer ' + token)});
    }

}


